import {
  DateRange,
  DefaultMetricOptions,
  Goal,
  GoalSortingMode,
  Habit,
  TaskType,
} from '@griply/types';
import { sortGoals } from '@griply/utils';
import { isAfter, isBefore } from 'date-fns';
import { useGoals, useSubGoals } from 'features/goals';
import { useTasksByGoalId } from 'features/tasks';
import React, { useMemo } from 'react';
import { GoalInsightsCard } from 'shared/components/connected/goal-insights-card';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { getSortedTasks } from 'shared/utils/get-sorted-tasks';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { useTheme } from 'styled-components';

import { HabitInsightsCard } from '../habit-insights-card';
import * as Styled from './progress-carousel.style';

export type ProgressCarouselProps = {
  goal: Goal;
  dateRange: DateRange;
};

export const ProgressCarousel: React.FC<ProgressCarouselProps> = ({
  goal,
  dateRange,
}) => {
  const theme = useTheme();
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  const goals = useGoals();
  const tasks = useTasksByGoalId(goal.id);
  const subGoals = useSubGoals(goal.id);

  const goalTasks = useMemo(
    () => tasks.filter(({ schedules }) => !schedules?.length),
    [tasks],
  );

  const goalHabits = useMemo(
    () =>
      getSortedTasks(
        tasks.filter(({ type }) => type === TaskType.Habit),
        {
          today,
          weekStartsOn,
          mode: goal?.taskSortingMode ? [goal?.taskSortingMode] : undefined,
          order: [
            ...(goal?.sections?.map(({ tasks }) => tasks).flat(1) ?? []),
            ...(goal?.taskSorting ?? []),
          ],
        },
      ) as Habit[],
    [
      goal?.sections,
      goal?.taskSorting,
      goal?.taskSortingMode,
      tasks,
      today,
      weekStartsOn,
    ],
  );

  const mainGoalHasMetric =
    goal.metric?.id &&
    (goal.metric?.id !== DefaultMetricOptions.CompletedTasks ||
      (!subGoals.length && !goalHabits.length) ||
      !!goalTasks.length);

  const sortedSubGoals = useMemo(
    () =>
      sortGoals(
        subGoals.filter(
          ({ completedAt, archivedAt, startDate, deadline }) =>
            !completedAt &&
            !archivedAt &&
            startDate &&
            deadline &&
            (isBefore(startDate, dateRange.endDate) ||
              isAfter(deadline, dateRange.startDate)),
        ),
        {
          allGoals: goals,
          sortMode: GoalSortingMode.Deadline,
        },
      ),
    [dateRange.endDate, dateRange.startDate, goals, subGoals],
  );

  const progressItems = useMemo(
    () =>
      [...sortedSubGoals, ...goalHabits]
        .sort()
        .slice(
          0,
          theme.isMobile
            ? mainGoalHasMetric
              ? 1
              : 2
            : mainGoalHasMetric
              ? 3
              : 4,
        ),
    [goalHabits, mainGoalHasMetric, sortedSubGoals, theme.isMobile],
  );

  const amountOfItems = progressItems.length + (mainGoalHasMetric ? 1 : 0);

  return (
    <Styled.Container>
      <Styled.List>
        {mainGoalHasMetric && (
          <Styled.ListItem $totalAmount={amountOfItems}>
            <GoalInsightsCard
              goal={goal}
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              noTitle
              hasBigTitle
              canUpdateProgress
            />
          </Styled.ListItem>
        )}

        {progressItems.map((item) => (
          <Styled.ListItem key={item.id} $totalAmount={amountOfItems}>
            {!('type' in item) && (
              <GoalInsightsCard
                key={item.id}
                goal={item}
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                hasBigTitle
                canUpdateProgress
                isLink
              />
            )}

            {'type' in item && (
              <HabitInsightsCard
                key={item.id}
                habit={item}
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
              />
            )}
          </Styled.ListItem>
        ))}
      </Styled.List>
    </Styled.Container>
  );
};
